import { css, html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { debounce } from '../modules/common';
import { ICONS } from '../modules/resources';
import { KryptonElement } from './krypton-element';

export interface Searchable<T> {

    searchEntries(): T[];

    searching(searching: boolean);

    searchStart();

    searchEnd();

}

function instanceofSearchable(object: any): object is Searchable<any> {
    return 'searchEntries' in object;
}


export class SearchMenu extends KryptonElement {

    static buffer: number = 500;
    _active: boolean = false;
    @property({ type: String }) accessor selector = "*";
    @property({ type: HTMLElement, reflect: true, converter: {} }) accessor target: Searchable<any>;

    static styles = css`
      :host {
          display: block;
          padding: 1rem 0;
          z-index: 9800;
          left: var(--z-index-krypton-base-nav-search);
          position: relative;
      }

      #search-input {
          width: 85%;
          margin: auto;
          display: block;
          outline: #a3a3a3;
          border-radius: 20px !important;
          background: url('${unsafeCSS(ICONS.search.file)}') white no-repeat 95% center;
          padding: 0.65rem 1rem;
          border: none;
          font-weight: normal;
      }

      #search-input.active {
          background: white;
      }

      #clear-input {
          position: absolute;
          right: calc(1rem + 24px);
          top: 1.4rem;
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          opacity: 0.25;
          display: none;
      }

      #clear-input.active {
          display: block;
      }

      #clear-input:focus,
      #clear-input:hover {
          outline: none;
      }
    `;

    render() {
        return html`
            <input type="text" id="search-input" autocomplete="off" placeholder="Menu Search" @keyup="${this.onKeyup}" @click="${this.preventMainMenuHide}"></input>
            <button id="clear-input" @click="${this.clear}">
                <img src="${ICONS.x.file}"/>
            </button>
        `;
    }

    preventMainMenuHide(e) {
        e && e.stopPropagation();
    }

    onKeyup(e: Event) {
        if (this.searchInput?.value && !this.searchInput.value.trim()) {
            this.searchInput.value = '';
        }
        this.active = !!this.searchInput.value;
        this.search();
    }

    connectedCallback() {
        let parent: any = this.parentElement;
        while (parent && !instanceofSearchable(parent)) {
            parent = parent.parentElement || (parent.parentNode as ShadowRoot)?.host;
        }
        this.target = parent as unknown as Searchable<any>;
        super.connectedCallback();
    }

    public search: Function = debounce((value: string) => {

        value = value || this.searchInput.value;

        const entries: HTMLElement[] = [].slice.call(this.target.searchEntries());

        //-- blank, clear all
        if (!value) {
            entries.forEach(entry => {
                entry.classList.remove("search-hit");
                entry.classList.remove("search-miss");
                entry.classList.remove("search-hit-parent");
                if (entry["category"] && entry.classList.contains("open")) {
                    entry["collapseOptions"]();
                }
            });
            this.target.searchStart();
            this.target.searchEnd();
            this.target.searching(false);
            return;
        }
        this.target.searching(true);
        this.target.searchStart();
        const results: HTMLElement[] = [];
        entries.forEach(entry => { if (this.match(entry)) results.push(entry) });
        this.target.searchEnd();
        return results;

    }, SearchMenu.buffer, this);

    match(entry: HTMLElement) {
        const selector = this.selector.replace(/\$0/, this.searchInput.value.toLowerCase());

        let containsMatchingChild = false;
        const children = [].slice.call(entry.querySelectorAll(":scope > " + selector));
        children.forEach(c => {
            if (this.match(c)) containsMatchingChild = true;
        });

        if (containsMatchingChild) {
            entry.classList.add("search-hit-parent");
        } else {
            entry.classList.remove("search-hit-parent");
        }

        if (entry.matches(selector) && !entry["category"]) {
            entry.classList.remove("search-miss");
            entry.classList.add("search-hit");
            return true;
        } else {
            entry.classList.remove("search-hit");
            entry.classList.add("search-miss");
            return false;
        }
    }

    clear(e: Event) {
        this.searchInput.value = "";
        this.searchInput.classList.remove("active");
        this.clearInput.classList.remove("active");
        this.search();
    }

    get active() {
        return this._active;
    }

    @property({ type: Boolean, reflect: true })
    set active(active) {
        const oldValue = this._active;
        this._active = active;
        this.searchInput.classList[active ? "add" : "remove"]("active");
        this.clearInput.classList[active ? "add" : "remove"]("active");
        this.requestUpdate("active", oldValue);
    }

    get searchInput(): HTMLInputElement {
        return this.shadowRoot?.getElementById("search-input") as HTMLInputElement;
    }

    get clearInput() {
        return this.shadowRoot?.getElementById("clear-input") as HTMLButtonElement;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'search-menu': SearchMenu;
    }
}